import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Trash2, GripVertical, Asterisk, Database } from 'lucide-react';
import { Field, InputControl } from '../types';

interface SortableFieldProps {
  field: Field;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: (e: React.MouseEvent) => void;
}

export const SortableField: React.FC<SortableFieldProps> = ({ field, isSelected, onSelect, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const renderDatasourceInfo = () => {
      if (!field.datasourceString) return null;
      
      const parts = field.datasourceString.split('::');
      const source = parts[0];
      const hasConfig = parts.length > 1;
      
      return (
          <div className="mt-2 flex items-center gap-2">
              <div className="inline-flex items-center gap-1.5 text-[10px] bg-teal-50 text-teal-700 px-2 py-1 rounded-md border border-teal-100 max-w-full" title={field.datasourceString}>
                  <Database size={10} className="shrink-0" />
                  <span className="font-semibold truncate">{source}</span>
                  {hasConfig && (
                      <span className="text-teal-500 text-[9px] shrink-0 border-l border-teal-200 pl-1.5 ml-0.5">
                         +{parts.length - 1} config
                      </span>
                  )}
              </div>
          </div>
      );
  };

  const commonInputClass = "w-full border border-slate-300 rounded-lg bg-white text-slate-400 text-sm";

  const renderPreviewInput = () => {
    switch (field.inputControl) {
        case InputControl.TEXT:
            return <div className={`h-10 px-3 flex items-center ${commonInputClass}`}>Text input...</div>;
        case InputControl.TEXTAREA:
             return <div className={`h-20 px-3 py-2 ${commonInputClass}`}>Text area...</div>;
        case InputControl.SELECT:
             return (
                 <div className={`h-10 px-3 flex items-center justify-between ${commonInputClass}`}>
                    <span>Select an option</span>
                    <span>▼</span>
                 </div>
             );
        case InputControl.CHECKBOX:
            return (
                <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border border-slate-300 rounded bg-white"></div>
                    <span className="text-slate-500 text-sm">{field.label}</span>
                </div>
            );
        case InputControl.RADIO:
            return (
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                         <div className="w-4 h-4 rounded-full border border-slate-300 bg-white"></div>
                         <span className="text-slate-400 text-sm">Option 1</span>
                    </div>
                    <div className="flex items-center gap-2">
                         <div className="w-4 h-4 rounded-full border border-slate-300 bg-white"></div>
                         <span className="text-slate-400 text-sm">Option 2</span>
                    </div>
                </div>
            );
        case InputControl.DATEPICKER:
            return <div className={`h-10 px-3 flex items-center font-mono ${commonInputClass}`}>DD/MM/YYYY</div>;
        case InputControl.BUTTON:
            return (
                <div className="flex justify-start">
                    <button className="h-10 px-4 bg-teal-50 text-teal-600 border border-teal-200 rounded-lg text-sm font-medium shadow-sm hover:bg-teal-100 transition-colors pointer-events-none">
                        Open Dialog
                    </button>
                </div>
            );
        case InputControl.READONLY:
             return (
                <div className="h-10 w-full bg-slate-100 border border-slate-300 rounded-lg px-3 flex items-center text-slate-500 text-sm italic font-mono">
                     {field.parameterConfig?.routeKey ? `Route Param: ${field.parameterConfig.routeKey}` : 'Route Param: <unconfigured>'}
                </div>
             );
        default:
            return <div className="h-10 w-full bg-white border border-slate-300 rounded-lg"></div>;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      className={`group relative p-4 rounded-xl border transition-all cursor-pointer bg-white ${
        isSelected 
          ? 'border-teal-500 shadow-sm ring-1 ring-teal-500/20' 
          : 'border-slate-200 hover:border-teal-400 hover:shadow-sm'
      }`}
    >
      <div className="absolute left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing p-1 text-slate-300 hover:text-slate-500 transition-opacity"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={20} />
      </div>

      <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button 
            onClick={onDelete}
            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
            <Trash2 size={16} />
        </button>
      </div>

      <div className="pl-6">
        {field.inputControl !== InputControl.CHECKBOX && (
            <label className="block text-sm font-bold text-slate-700 mb-1.5">
                {field.label}
                {field.validation?.required && <Asterisk size={10} className="inline text-red-500 ml-0.5 align-top" />}
            </label>
        )}
        
        {renderPreviewInput()}

        {renderDatasourceInfo()}
        
        <div className="mt-3 flex items-center gap-2">
            <span className="text-[10px] text-slate-500 font-mono bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">#{field.id}</span>
            <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wide">{field.type}</span>
        </div>
      </div>
    </div>
  );
};